import React, { Fragment, createElement } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SharjahPoliceEmblem } from '../SharjahPoliceEmblem';
import { ShieldCheckIcon, BuildingIcon, Building2Icon, UserIcon, ClockIcon, AlertTriangleIcon, PhoneIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const PAIR2_LOGO = 'https://cdn.magicpatterns.com/uploads/51QRCG1WJpxN6EwND9yhQF/Pair2_(2).svg';
const NODES = [{
  id: 'police',
  label: 'جهة حكومية',
  icon: ShieldCheckIcon,
  color: '#2FA99C'
}, {
  id: 'leasing',
  label: 'شركة التأجير',
  icon: BuildingIcon,
  color: '#6E7A8F'
}, {
  id: 'operator',
  label: 'شركة التشغيل',
  icon: Building2Icon,
  color: '#6E7A8F'
}, {
  id: 'driver',
  label: 'السائق',
  icon: UserIcon,
  color: '#6E7A8F'
}];
const CONNECTIONS = [{
  label: 'مراسلات رسمية',
  delay: 'تأخير',
  icon: ClockIcon
}, {
  label: 'بحث في السجلات',
  delay: 'انتظار',
  icon: PhoneIcon
}, {
  label: 'تحقق ميداني',
  delay: 'غير مؤكد',
  icon: AlertTriangleIcon
}];
export function ChallengeSlide() {
  const reduce = useReducedMotion();
  const base = reduce ? {
    duration: 0
  } : undefined;
  return <section dir="rtl" className="relative w-screen h-screen overflow-hidden flex flex-col" style={{
    background: '#EDF1F4'
  }} data-id="element-171">
      {/* ===== BACKGROUND ===== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden data-id="element-172">
        {/* Soft red/orange glow to hint at friction/challenge */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vmax] h-[60vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(212,85,58,0.035) 0%, transparent 60%)'
      }} data-id="element-173" />
        <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h48v48H0V0zm1 1h46v46H1V1z' fill='%230B1328' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
      }} data-id="element-174" />
      </div>

      {/* ===== TOP BAR ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 0.1,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative z-20 flex items-center justify-between pt-[clamp(1rem,2.5vh,2rem)] px-[clamp(1.5rem,4vw,4rem)]" data-id="element-175">
        <SharjahPoliceEmblem variant="light" data-id="element-176" />
        <img src={PAIR2_LOGO} alt="Pair²" className="h-[clamp(2.25rem,4.5vh,3.25rem)] w-auto" style={{
        filter: 'drop-shadow(0 4px 18px rgba(47,169,156,0.28))'
      }} draggable={false} data-id="element-177" />
      </motion.div>

      {/* ===== TITLE (same style as GapSlide) ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 0.2
    }} className="relative z-10 px-[clamp(2rem,6vw,6rem)] mt-[clamp(0.75rem,2vh,1.5rem)]" data-id="element-178">
        <h2 className="font-arabic-display font-black text-center leading-[1.15]" style={{
        color: '#0B1328',
        fontSize: 'clamp(1.4rem, 2.3vw, 2.1rem)'
      }} data-id="element-179">
          محدوديه انظمة التتبع المتاحه
        </h2>
        <div className="mx-auto mt-2.5 h-[2.5px] rounded-full" style={{
        width: 'clamp(3rem, 5vw, 4rem)',
        background: '#2FA99C',
        opacity: 0.45
      }} data-id="element-180" />
        <p className="font-arabic-body text-center mt-3" style={{
        color: '#6E7A8F',
        fontSize: 'clamp(0.9rem, 1.2vw, 1.1rem)'
      }} data-id="element-181">
          آلية الوصول إلى بيانات السائق الذي كان يقود مركبة في وقت محدد
        </p>
      </motion.div>

      {/* ===== MAIN CONTENT ===== */}
      <div className="relative z-10 flex-1 flex flex-col justify-center px-[clamp(2rem,6vw,8rem)] min-h-0" data-id="element-182">
        {/* Flow Diagram */}
        <div className="flex items-center justify-between w-full" dir="rtl" data-id="element-183">
          {NODES.map((node, i) => <Fragment key={node.id} data-id="element-184">
              {/* Node Card */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={base ?? {
            duration: 0.6,
            delay: 0.4 + i * 0.3,
            type: 'spring',
            bounce: 0.3
          }} className="flex flex-col items-center justify-center shrink-0" style={{
            width: 'clamp(6rem, 10vw, 9rem)'
          }} data-id="element-185">
                <div className="flex items-center justify-center rounded-2xl mb-[clamp(0.75rem,2vh,1rem)] relative" style={{
              width: 'clamp(3.5rem, 6vw, 4.5rem)',
              height: 'clamp(3.5rem, 6vw, 4.5rem)',
              background: node.id === 'police' ? 'rgba(47,169,156,0.08)' : 'rgba(255,255,255,0.9)',
              border: `1px solid ${node.id === 'police' ? 'rgba(47,169,156,0.3)' : 'rgba(26,31,43,0.08)'}`,
              boxShadow: '0 6px 20px -6px rgba(14,93,85,0.06)'
            }} data-id="element-186">
                  <node.icon size={28} color={node.color} strokeWidth={1.5} data-id="element-187" />
                </div>
                <span className="font-arabic-body font-bold text-center text-[#0B1328] text-[clamp(0.85rem,1.1vw,1rem)]" data-id="element-188">
                  {node.label}
                </span>
              </motion.div>

              {/* Connection (between nodes) */}
              {i < NODES.length - 1 && <motion.div initial={{
            opacity: 0,
            scaleX: 0
          }} animate={{
            opacity: 1,
            scaleX: 1
          }} transition={base ?? {
            duration: 0.6,
            delay: 0.55 + i * 0.3,
            ease: 'easeOut'
          }} className="flex-1 flex flex-col items-center justify-center relative px-[clamp(0.5rem,1.5vw,1.5rem)] origin-right" data-id="element-189">
                  {/* Label (above arrows) */}
                  <span className="text-[#1A1F2B]/60 text-[clamp(0.65rem,0.8vw,0.75rem)] mb-[clamp(0.4rem,1vh,0.75rem)] font-medium text-center" data-id="element-190">
                    {CONNECTIONS[i].label}
                  </span>

                  {/* Arrows */}
                  <div className="w-full relative flex flex-col gap-1.5" data-id="element-191">
                    {/* Top arrow (Request flowing Left) */}
                    <div className="w-full flex items-center" data-id="element-192">
                      <div className="h-[2px] flex-1 border-t-2 border-dashed border-[#D4553A]/50" data-id="element-193" />
                      <ChevronLeftIcon size={18} className="text-[#D4553A]/60 -ml-2 shrink-0" data-id="element-194" />
                    </div>
                    {/* Bottom arrow (Response flowing Right) */}
                    <div className="w-full flex items-center opacity-60" data-id="element-195">
                      <ChevronRightIcon size={18} className="text-[#D4553A]/40 -mr-2 shrink-0" data-id="element-196" />
                      <div className="h-[2px] flex-1 border-t border-dotted border-[#D4553A]/40" data-id="element-197" />
                    </div>
                  </div>

                  {/* Friction Pill (below arrows) */}
                  <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={base ?? {
              duration: 0.4,
              delay: 0.75 + i * 0.3
            }} className="flex items-center gap-1.5 bg-[#D4553A]/10 border border-[#D4553A]/20 px-2.5 py-1 rounded-full mt-[clamp(0.4rem,1vh,0.75rem)]" data-id="element-198">
                    {createElement(CONNECTIONS[i].icon, {
                size: 12,
                className: 'text-[#D4553A]'
              })}
                    <span className="text-[#D4553A] text-[clamp(0.65rem,0.8vw,0.75rem)] font-bold" data-id="element-199">
                      {CONNECTIONS[i].delay}
                    </span>
                  </motion.div>
                </motion.div>}
            </Fragment>)}
        </div>

        {/* Bottom Highlight */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={base ?? {
        duration: 0.8,
        delay: 2.0
      }} className="w-full flex justify-center mt-[clamp(3rem,8vh,5rem)]" data-id="element-200">
          <div className="bg-[#D4553A]/10 border border-[#D4553A]/20 px-[clamp(1.5rem,3vw,2.5rem)] py-[clamp(0.75rem,1.5vh,1rem)] rounded-full shadow-sm" data-id="element-201">
            <p className="font-arabic-body font-bold text-[#D4553A] text-[clamp(0.95rem,1.2vw,1.15rem)] text-center" data-id="element-202">
              لا توجد رؤية مباشرة تربط المركبة بالسائق الفعلي في الوقت المطلوب
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}